import { useState, useEffect, useCallback, useRef } from "react";
import { Event } from "../../models/api/nylas";
import { useFetchEvents, useFetchMyEvents } from "../../api/nylas";
import { EventInput } from "@fullcalendar/core";
import { ParsedWhenObject, parseWhenObject } from "../../utils/nylasEventWhenObjectParser";
import useCurrentUser from "../../auth/useCurrentUser";

function isParseWhenObjectResult(result: ReturnType<typeof parseWhenObject>): result is ParsedWhenObject {
    return (result as ParsedWhenObject).start !== undefined;
}

function formatNylasEvents(eventsData: Event[]): EventInput[] {
    return eventsData
        .map((event: Event) => {
            const parseResult = parseWhenObject(event.when);

            if (isParseWhenObjectResult(parseResult)) {
                const { start: parsedStart, end: parsedEnd, timezone, isAllDay } = parseResult;
                return {
                    id: event.id,
                    title: event.title,
                    start: parsedStart,
                    end: parsedEnd,
                    allDay: isAllDay,
                    extendedProps: {
                        calendarId: event.calendar_id,
                        description: event.description || "",
                        location: event.location || "",
                        metaData: event.metadata,
                        timezone: timezone,
                    },
                } as EventInput;
            } else {
                console.error(`Error parsing event '${event.title}': `, parseResult.message);
                return null;
            }
        })
        .filter((event): event is EventInput => event !== null);
}

type PendingCalendarFetch = {
    start: number;
    end: number;
    success: (events: EventInput[]) => void;
    failure: (error: Error) => void;
};

function useNylasCalendar(talentId?: string) {
    const [timeRange, setTimeRange] = useState<{ start?: number; end?: number }>({});
    const { refetchCurrentUser } = useCurrentUser();
    const reconcileAfterErrorRef = useRef(false);
    const pendingCalendarRef = useRef<PendingCalendarFetch | null>(null);

    const { data: eventsData, error, mutate, isValidating } = talentId
        ? useFetchEvents(talentId, timeRange.start, timeRange.end)
        : useFetchMyEvents(timeRange.start, timeRange.end);

    // When events fail (bad grant/calendar IDs, revoked provider, etc.), refresh /me once so server
    // calendar health runs and the nav indicator + NylasAuth stay aligned; then retry events.
    useEffect(() => {
        if (!error) {
            reconcileAfterErrorRef.current = false;
            return;
        }
        if (reconcileAfterErrorRef.current) return;
        reconcileAfterErrorRef.current = true;
        void (async () => {
            try {
                await refetchCurrentUser();
                await mutate();
            } catch {
                // refetch/mutate errors are already surfaced via SWR `error`
            }
        })();
    }, [error, refetchCurrentUser, mutate]);

    // FullCalendar needs success/failure when SWR has finished for the requested range. Do not call
    // mutate() from the range setter: it revalidates the *previous* key before React applies setTimeRange,
    // which duplicates requests. Also avoid putting FullCalendar's `events` callback in a dependency array
    // that includes loaded events — a new function reference makes FullCalendar re-fetch the same range.
    useEffect(() => {
        const pending = pendingCalendarRef.current;
        if (!pending) return;

        if (error) {
            pendingCalendarRef.current = null;
            pending.failure(new Error("Failed to load calendar events"));
            return;
        }

        if (isValidating) return;

        if (eventsData === undefined) return;

        if (timeRange.start !== pending.start || timeRange.end !== pending.end) return;

        pendingCalendarRef.current = null;
        pending.success(formatNylasEvents(eventsData));
    }, [eventsData, error, isValidating, timeRange.start, timeRange.end]);

    const fetchEvents = useCallback((start: number, end: number, success: (e: EventInput[]) => void, failure: (e: Error) => void) => {
        pendingCalendarRef.current = { start, end, success, failure };
        setTimeRange({ start, end });
    }, []);

    return { error, fetchEvents };
}

export default useNylasCalendar;
